// src/components/MotionCard.js
// Updated: 11/09/2025 08:56 PM EST
// --- THIS IS THE FULL, UPDATED FILE ---

import React, { useState, useEffect, useMemo, useRef } from 'react';
// --- 1. Import 'functions' and 'httpsCallable' ---
import { db, functions } from '../firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../context/AuthContext';
import {
  doc,
  getDoc,
  runTransaction,
  serverTimestamp,
  // --- 2. Remove 'updateDoc' ---
  collection,
  query,
  getDocs,
  orderBy,
} from 'firebase/firestore';
import { Check, X, Circle, Lock, UserX, Users, Printer, Eye, EyeOff, RefreshCw } from 'lucide-react';

// --- 3. Define new callable functions ---
const adminChangeMotionStatus = httpsCallable(functions, 'adminChangeMotionStatus');
const adminChangeMotionPublicStatus = httpsCallable(functions, 'adminChangeMotionPublicStatus');

// Simple loading spinner
const MiniSpinner = () => <div className="h-4 w-4 animate-spin rounded-full border-2 border-dashed border-gray-400"></div>;

export default function MotionCard({ motion }) {
  const { currentUser, userData } = useAuth();
  const [voted, setVoted] = useState(null);
  const [isLoadingVoteStatus, setIsLoadingVoteStatus] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const cardRef = useRef(null);

  const [voterData, setVoterData] = useState([]);
  const [isLoadingVoterData, setIsLoadingVoterData] = useState(false);

  const [isPublic, setIsPublic] = useState(motion.resultsPublic || false);
  const [isTogglingPublic, setIsTogglingPublic] = useState(false);

  const isClosed = motion.status === 'closed';
  const isAdmin = userData?.role === 'admin';
  const isNamedVote = motion.voteType === 'named';
  const wasRestricted = useMemo(() => motion.allowedVoters && Array.isArray(motion.allowedVoters), [motion.allowedVoters]);

  const isAllowedToVote = useMemo(() => {
    if (!currentUser) return false;
    if (!motion.allowedVoters || !Array.isArray(motion.allowedVoters)) {
      return true; // Unrestricted motion
    }
    return motion.allowedVoters.includes(currentUser.uid); // Restricted motion
  }, [motion.allowedVoters, currentUser]);

  // --- No changes to useEffect hooks ---
  useEffect(() => {
    const checkVoteStatus = async () => {
      setIsLoadingVoteStatus(true);
      if (!currentUser || !motion.id) {
        setIsLoadingVoteStatus(false);
        return;
      }
      const voteDocRef = doc(db, 'motions', motion.id, 'votes', currentUser.uid);
      try {
        const voteDocSnap = await getDoc(voteDocRef);
        setVoted(voteDocSnap.exists() ? voteDocSnap.data().choice : null);
      } catch (error) {
        console.error('Error checking vote status:', error);
        setVoted(null);
      }
      finally { setIsLoadingVoteStatus(false); }
    };
    checkVoteStatus();
  }, [motion.id, currentUser]);

  useEffect(() => {
    if (isClosed && isAdmin && motion.id) {
      const fetchVoters = async () => {
        setIsLoadingVoterData(true);
        setVoterData([]);
        try {
          const votesQuery = query(
            collection(db, 'motions', motion.id, 'votes'),
            orderBy('votedAt', 'asc')
          );
          const querySnapshot = await getDocs(votesQuery);
          const votes = [];
          querySnapshot.forEach((doc) => {
            const data = doc.data();
            votes.push({
              id: doc.id,
              voterName: data.voterName || 'Unknown Voter',
              choice: data.choice,
              votedAt: data.votedAt,
            });
          });
          setVoterData(votes);
        } catch (error) {
          console.error("Error fetching detailed voter data for admin:", error);
        } finally {
          setIsLoadingVoterData(false);
        }
      };
      fetchVoters();
    } else {
      setVoterData([]);
      setIsLoadingVoterData(false);
    }
  }, [isClosed, isAdmin, motion.id]);

  useEffect(() => {
    setIsPublic(motion.resultsPublic || false);
  }, [motion.resultsPublic]);
  // --- End of useEffect hooks ---


  // Handle the vote submission (No changes)
  const handleVote = async (choice) => {
    if (voted || isSubmitting || motion.status === 'closed' || !isAllowedToVote) return;
    setIsSubmitting(true);
    const motionRef = doc(db, 'motions', motion.id);
    const voteRef = doc(db, 'motions', motion.id, 'votes', currentUser.uid);
    const impersonatingAdminId = sessionStorage.getItem('impersonating_admin_id');
    try {
      await runTransaction(db, async (transaction) => {
        const motionSnap = await transaction.get(motionRef);
        if (!motionSnap.exists() || motionSnap.data().status !== 'open') {
          throw new Error('Voting is closed for this motion.');
        }
        const currentMotionData = motionSnap.data();
        if (currentMotionData.allowedVoters && !currentMotionData.allowedVoters.includes(currentUser.uid)) {
          throw new Error('You are not authorized to vote on this motion.');
        }
        const voteDoc = await transaction.get(voteRef);
        if (voteDoc.exists()) { throw new Error('You have already voted on this motion.'); }
        const voteData = {
          userId: currentUser.uid,
          choice: choice,
          votedAt: serverTimestamp(),
          voterName: currentMotionData.voteType === 'named' ? currentUser.displayName || 'User' : 'Anonymous',
          orgId: currentMotionData.orgId,
          ...(impersonatingAdminId && { impersonatedBy: impersonatingAdminId }),
        };
        transaction.set(voteRef, voteData);
        const incrementField = `results.${choice}`;
        const currentChoiceCount = currentMotionData.results?.[choice] || 0;
        const currentTotal = currentMotionData.results?.total || 0;
        transaction.update(motionRef, {
          [incrementField]: currentChoiceCount + 1, 'results.total': currentTotal + 1,
        });
      });
      setVoted(choice);
    } catch (e) {
      console.error('Vote Transaction Error:', e);
      if (!e.message.includes('already voted') && !e.message.includes('Voting is closed') && !e.message.includes('not authorized')) {
        alert(`Vote failed: ${e.message}`);
      }
      if (e.message.includes('not authorized')) { console.warn("Vote blocked by transaction check..."); }
    } finally { setIsSubmitting(false); }
  };

  // --- 4. UPDATE Admin-only function to close voting ---
  const handleCloseVoting = async () => {
    if (!isAdmin || isSubmitting || motion.status !== 'open') return;
    if (!window.confirm("Are you sure you want to close voting for this motion?")) return;
    setIsSubmitting(true);
    try {
      // Use the callable function
      await adminChangeMotionStatus({
        motionId: motion.id,
        newStatus: 'closed',
        orgId: motion.orgId
      });
      // UI will update via onSnapshot
    }
    catch (e) {
      console.error('Error closing motion:', e);
      alert(`Failed to close motion: ${e.message}`);
    }
    finally { setIsSubmitting(false); }
  };

  // --- 5. UPDATE Admin function to toggle public results visibility ---
  const handleTogglePublic = async () => {
    if (!isAdmin || !isClosed || wasRestricted || isTogglingPublic) return;
    setIsTogglingPublic(true);
    const newPublicStatus = !isPublic;
    try {
      // Use the callable function
      await adminChangeMotionPublicStatus({
        motionId: motion.id,
        newPublicStatus: newPublicStatus,
        orgId: motion.orgId
      });
      setIsPublic(newPublicStatus); // Optimistic update
    } catch (e) {
      console.error('Error toggling public status:', e);
      alert(`Failed to update visibility status: ${e.message}`);
    } finally {
      setIsTogglingPublic(false);
    }
  };

  // --- No changes to Print Handler or Timestamp Formatter ---
  const handlePrint = () => {
    const cardElement = cardRef.current;
    if (!cardElement) return;
    document.body.classList.add('printing-motion-card');
    cardElement.classList.add('print-this-card');
    window.print();
    setTimeout(() => {
      document.body.classList.remove('printing-motion-card');
      cardElement.classList.remove('print-this-card');
    }, 50);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp?.toDate) return '...';
    return timestamp.toDate().toLocaleString('en-US', {
      year: 'numeric', month: 'numeric', day: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true
    });
  };

  // --- No changes to JSX ---
  return (
    <div ref={cardRef} className="motion-card rounded-lg bg-white shadow-lg print:shadow-none print:border print:border-gray-300">
      {/* Header section */}
      <div className="p-6 print:p-4">
        <div className="flex items-center justify-between gap-4">
          <h3 className="text-xl font-semibold text-gray-900 print:text-lg">{motion.title}</h3>
          <div className="flex items-center space-x-2 flex-shrink-0 print:hidden">
            {wasRestricted && (
              <span title={`Voting restricted to specific member(s)`} className="flex items-center rounded-full bg-yellow-100 px-2 py-1 text-xs font-medium text-yellow-800">
                <UserX className="h-3 w-3 mr-1" /> Restricted
              </span>
            )}
            <span className={`rounded-full px-3 py-1 text-xs font-medium ${isClosed ? 'bg-gray-200 text-gray-800' : 'bg-green-100 text-green-800'}`}>
              {isClosed ? 'Closed' : 'Active'}
            </span>
          </div>
          <div className="hidden print:block text-sm font-medium"> Status: {isClosed ? 'Closed' : 'Active'} {wasRestricted ? '(Restricted Voting)' : ''} </div>
        </div>
        {motion.description && (<p className="mt-2 text-gray-600 print:text-sm">{motion.description}</p>)}
        <p className="mt-2 text-sm text-gray-400 print:text-xs"> Vote Type: {isNamedVote ? 'Named' : 'Secret'} </p>
        {motion.createdAt && (<p className="mt-1 text-xs text-gray-400"> Created: {motion.createdAt.toDate().toLocaleDateString()} </p>)}
      </div>

      {/* Voting Interface or Results Section */}
      <div className="border-t border-gray-200 bg-gray-50 p-6 print:border-0 print:bg-white print:p-4">
        {isClosed ? (
          // --- RESULTS VIEW ---
          <div>
            {/* Admin View Header */}
            {isAdmin && (
              <div className="flex justify-between items-center mb-3">
                <h4 className="text-lg font-semibold print:text-base">Results:</h4>
                <div className="flex items-center space-x-3 print:hidden">
                  {!wasRestricted && (
                    <button
                      onClick={handleTogglePublic}
                      disabled={isTogglingPublic}
                      className={`flex items-center rounded px-2 py-1 text-sm ${isPublic
                          ? 'text-green-600 hover:bg-green-50'
                          : 'text-gray-500 hover:bg-gray-100'
                        } disabled:opacity-50 disabled:cursor-wait`}
                      title={isPublic ? 'Make results private to voters/admins' : 'Make results public to all org members'}
                    >
                      {isTogglingPublic ? (
                        <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                      ) : isPublic ? (
                        <Eye className="h-4 w-4 mr-1" />
                      ) : (
                        <EyeOff className="h-4 w-4 mr-1" />
                      )}
                      {isPublic ? 'Public' : 'Private'}
                    </button>
                  )}
                  <button
                    onClick={handlePrint}
                    className="flex items-center rounded px-2 py-1 text-sm text-gray-500 hover:bg-gray-200 hover:text-gray-700"
                    title="Print Motion Results"
                  >
                    <Printer className="h-4 w-4 mr-1" /> Print
                  </button>
                </div>
              </div>
            )}
            {/* Member View Header */}
            {!isAdmin && (<h4 className="text-lg font-semibold mb-3">Motion Closed</h4>)}

            {/* Aggregate Results */}
            <div className="space-y-2 border-b pb-4 mb-4">
              <div className="flex items-center justify-between"> <span className="text-green-700">Yes</span> <span className="font-bold">{motion.results?.yes || 0}</span> </div>
              <div className="flex items-center justify-between"> <span className="text-red-700">No</span> <span className="font-bold">{motion.results?.no || 0}</span> </div>
              <div className="flex items-center justify-between"> <span className="text-gray-700">Abstain</span> <span className="font-bold">{motion.results?.abstain || 0}</span> </div>
              <div className="mt-3 border-t pt-2 font-semibold"> <div className="flex items-center justify-between"> <span>Total Votes</span> <span>{motion.results?.total || 0}</span> </div> </div>
            </div>

            {isAdmin && (
              <div>
                <h5 className="text-md font-semibold mb-2 flex items-center text-gray-700 print:text-sm">
                  <Users className="h-4 w-4 mr-2" /> {isNamedVote ? 'Individual Votes:' : 'Voter Details (Secret Ballot):'}
                </h5>
                {isLoadingVoterData ? (
                  <div className="flex justify-center py-4 print:hidden"><MiniSpinner /></div>
                ) : voterData.length > 0 ? (
                  <ul className="max-h-60 overflow-y-auto space-y-2 rounded-md border bg-white p-3 print:max-h-none print:overflow-visible print:border-none print:p-0">
                    {voterData.map(vote => (
                      <li key={vote.id} className="flex flex-col sm:flex-row justify-between sm:items-center text-sm border-b pb-1 last:border-b-0 print:border-gray-200 print:pb-2 print:mb-2">
                        <span className="text-gray-800 font-medium">{vote.voterName}</span>
                        <div className="flex items-center space-x-2 mt-1 sm:mt-0">
                          {vote.votedAt && (<span className="text-xs text-gray-500 print:text-gray-600">{formatTimestamp(vote.votedAt)}</span>)}
                          <span className={`font-semibold rounded px-1.5 py-0.5 text-xs ${vote.choice === 'yes' ? 'bg-green-100 text-green-700 print:bg-transparent print:text-green-700' :
                              vote.choice === 'no' ? 'bg-red-100 text-red-700 print:bg-transparent print:text-red-700' :
                                'bg-gray-100 text-gray-600 print:bg-transparent print:text-gray-600'
                            }`}>
                            {vote.choice ? vote.choice.toUpperCase() : 'N/A'}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500 italic">No votes were recorded for this motion.</p>
                )}
              </div>
            )}

            {/* Display User's Vote (If they voted and not admin) */}
            {!isAdmin && (
              isLoadingVoteStatus ? (
                <div className="mt-4 flex justify-center"><MiniSpinner /></div>
              ) : voted ? (
                <div className="mt-4 text-center p-2 border rounded bg-white">
                  <p className="font-semibold text-gray-700">
                    You voted:
                    <span className={`ml-2 font-bold ${voted === 'yes' ? 'text-green-600' : voted === 'no' ? 'text-red-600' : 'text-gray-600'}`}>
                      {voted.toUpperCase()}
                    </span>
                  </p>
                </div>
              ) : (
                <p className="mt-4 text-sm text-gray-500 italic">You did not vote on this motion.</p>
              )
            )}

          </div> // End Results View wrapper
        ) : (
          // --- VOTING VIEW ---
          <div className="print:hidden">
            {isLoadingVoteStatus ? (
              <p className="text-center text-gray-500">Loading vote status...</p>
            ) : !isAllowedToVote ? (
              <div className="text-center">
                <p className="font-semibold text-yellow-700 flex items-center justify-center">
                  <UserX className="h-5 w-5 mr-2" />
                  Voting on this motion is restricted.
                </p>
              </div>
            ) : voted ? (
              <div className="text-center">
                <p className="font-semibold text-gray-700">
                  You voted:
                  <span className={`ml-2 font-bold ${voted === 'yes' ? 'text-green-600' : voted === 'no' ? 'text-red-600' : 'text-gray-600'}`}>
                    {voted.toUpperCase()}
                  </span>
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-4">
                <button
                  onClick={() => handleVote('yes')}
                  disabled={isSubmitting}
                  className="flex flex-col items-center justify-center rounded-lg border-2 border-green-500 p-4 text-green-600 transition hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  aria-label="Vote Yes"
                >
                  <Check className="h-8 w-8" />
                  <span className="mt-2 font-semibold">Yes</span>
                </button>
                <button
                  onClick={() => handleVote('no')}
                  disabled={isSubmitting}
                  className="flex flex-col items-center justify-center rounded-lg border-2 border-red-500 p-4 text-red-600 transition hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  aria-label="Vote No"
                >
                  <X className="h-8 w-8" />
                  <span className="mt-2 font-semibold">No</span>
                </button>
                <button
                  onClick={() => handleVote('abstain')}
                  disabled={isSubmitting}
                  className="flex flex-col items-center justify-center rounded-lg border-2 border-gray-500 p-4 text-gray-600 transition hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  aria-label="Abstain from voting"
                >
                  <Circle className="h-8 w-8" />
                  <span className="mt-2 font-semibold">Abstain</span>
                </button>
              </div>
            )}

            {/* Admin "Close Voting" Button */}
            {isAdmin && !isClosed && (
              <div className="mt-6 border-t pt-4">
                <button
                  onClick={handleCloseVoting}
                  disabled={isSubmitting}
                  className="flex w-full items-center justify-center rounded-md bg-gray-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Lock className="mr-2 h-4 w-4" />
                  Close Voting
                </button>
              </div>
            )}
          </div> // End Voting View wrapper
        )}
      </div> {/* End Voting/Results section */}
    </div> // End component wrapper
  );
}